import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@/components/common/ResponsiveTable';
import translate from '@/lib/intl/translate';
import { PledgeDataPropType } from '@/lib/types/DonationPropTypes';
import { formatCurrency } from '@/utils/currency';
import { formatDate } from '@/utils/dates';

const CLASSES = {
  tableCell: {
    root: 'body1 font-normal',
    label: 'type-body2 font-medium',
  },
  viewButton: {
    label: 'p-0 type-body2',
    root: 'min-w-0 p-0',
  },
};

export default function OutstandingPledgesTable({
  donations,
  isLagging,
  onViewClick,
}) {
  const handleViewClick = (e) => {
    const { currentTarget } = e;

    onViewClick(currentTarget.getAttribute('data-donation-id'));
  };

  return (
    <TableContainer className="mt-2" data-testid="outstanding-pledges-table">
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>{translate('DONATION_AMOUNT_RECONCILED')}</TableCell>
            <TableCell>{translate('DONATION_DATE_PLEDGED')}</TableCell>
            <TableCell>{translate('DONATION_DATE_DUE')}</TableCell>
            <TableCell>{translate('UPDATED')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody className={classNames({ 'opacity-60': isLagging })}>
          {donations.map(
            ({
              donationId,
              donationDateString,
              pledgeData: {
                amountPledged,
                amountReceived,
                pledgeDueDateString,
              },
              updated,
            }) => (
              <TableRow
                key={donationId}
                className="last:border-0 hover:cursor-pointer hover:bg-grey-300"
                data-testid={`outstanding-pledges-table-${donationId}-view-button`}
                data-donation-id={donationId}
                onClick={handleViewClick}
              >
                <TableCell
                  classes={CLASSES.tableCell}
                  label={translate('DONATION_AMOUNT_RECONCILED_MOBILE')}
                >
                  {formatCurrency(amountReceived, true)}/
                  <span className="body2 font-medium">
                    {formatCurrency(amountPledged, true)}
                  </span>
                </TableCell>
                <TableCell
                  classes={CLASSES.tableCell}
                  label={translate('DONATION_DATE_PLEDGED_MOBILE')}
                >
                  {formatDate(donationDateString)}
                </TableCell>
                <TableCell
                  classes={CLASSES.tableCell}
                  label={translate('DONATION_DATE_DUE_MOBILE')}
                >
                  {formatDate(pledgeDueDateString)}
                </TableCell>
                <TableCell
                  classes={CLASSES.tableCell}
                  label={translate('UPDATED')}
                >
                  {formatDate(updated)}
                </TableCell>
              </TableRow>
            ),
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

OutstandingPledgesTable.propTypes = {
  donations: PropTypes.arrayOf(
    PropTypes.shape({
      donationId: PropTypes.string.isRequired,
      pledgeData: PledgeDataPropType.isRequired,
    }),
  ).isRequired,
  isLagging: PropTypes.bool,
  onViewClick: PropTypes.func.isRequired,
};

OutstandingPledgesTable.defaultProps = {
  isLagging: false,
};
